'use client';

import React from 'react';
import Link from 'next/link';
import { css, cx } from 'styled-system/css';

import useLocale from '@shaojiang/hooks/useLocale';
import { defaultLocale } from '@shaojiang/i18n/config';
import { Locale } from '@shaojiang/types/i18n';

type AnchorProps = Omit<React.ComponentPropsWithRef<'a'>, 'href'>;

interface Props extends AnchorProps {
  href: string;
  children: React.ReactNode;
  isActive?: boolean;
  locale?: Locale;
  disabled?: boolean;
  download?: boolean;
  inverted?: boolean;
}

const linkStyle = css({ display: 'inline-flex', alignItems: 'center' });

/** Locale-aware link: prefixes the current locale (except the default) and opens external links in a new tab. */
export default function ShaojiangLink({
  href,
  children,
  target = '_self',
  isActive = false,
  locale,
  className,
  disabled = false,
  download = false,
  inverted = false,
  ...rest
}: Props) {
  const { locale: currentLocale, pathName } = useLocale();
  const isExternal =
    href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto');

  const localeForLink = locale || currentLocale;
  const localeInUrl =
    localeForLink === '' || localeForLink === defaultLocale || isExternal
      ? ''
      : `/${localeForLink}`;

  if (disabled) {
    return (
      <span className={cx(linkStyle, className)} {...(rest as React.ComponentPropsWithRef<'span'>)}>
        {children}
      </span>
    );
  }

  return (
    <Link
      {...rest}
      download={download}
      href={`${localeInUrl}${href}`}
      target={isExternal ? '_blank' : target}
      rel={isExternal ? 'noopener noreferrer' : rest.rel}
      className={cx(
        linkStyle,
        (isActive || pathName === href) && css({ color: 'success.main' }),
        inverted && css({ color: 'white' }),
        className,
      )}
    >
      {children}
    </Link>
  );
}
